import { Controller } from '../utils/controller';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ymkAsyncInit: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    YMK: any;
  }
}

interface IFoundationShade {
  hex?: string;
  cooler?: string;
  lighter?: string;
  guid?: string;
  darker?: string;
  warmer?: string;
}

// Event types
const CHOOSE_MODEL_ICON_CLICKED_EVENT = 'chooseModelIconClicked';
const CLOSED_EVENT = 'closed';
const ENGINE_CLOSED_EVENT = 'engineClosed';
const LIVE_CAM_ICON_CLICKED_EVENT = 'liveCamIconClicked';
const LOAD_IMAGE_BY_MODEL_EVENT = 'loadImageByModel';
const LOADED_EVENT = 'loaded';
const LOADING_EVENT = 'loading';
const OPENED_EVENT = 'opened';
const SKIN_ANALYSIS_FINISHED_EVENT = 'skinAnalysisFinished';
const SKIN_SHADE_FOUND_EVENT = 'skinShadeFound';
const UPLOAD_PHOTO_ICON_CLICKED_EVENT = 'uploadPhotoIconClicked';
const VTO_ACTIONS_TRACKING_EVENT = 'VTOActionsTracking';

const ATB_BUTTON_ID = 'vto-add-to-bag-button';
const CAMERA = 'camera';
const CANVAS_ID = 'result-canvas';
const CTA_ACTION = 'cta';
const DESKTOP_BREAKPOINT = 1024;
const FOUNDATION = 'foundation';
const MODULE_IFRAME_ID = 'YMK-module-iframe';
const MODULE_VIDEO_INPUT_ID = 'YMK-video-input';
const MAKEUP = 'makeup';
const MOBILE_BREAKPOINT = 479;
const MODEL = 'model';
const MODULE_ID = 'YMK-module';
const SKINCARE = 'skincare';
const UPLOAD = 'upload';

const foundationShades: IFoundationShade[] = [
  { hex: '#e5ad8a' },
  { hex: '#f2bf91' },
  { hex: '#c48f56' },
  { hex: '#c27440' },
  { hex: '#d69060' }
];

export default class extends Controller {
  static targets = [
    'productCarousel',
    'vtoContainer',
    'launchButton',
    'vtoMobileWrapper',
    'vtoShadeCarousel',
    'variantShade',
    'shadesDropdownText',
    'shadesDropdownSelector',
    'trigger'
  ];
  static requiredTargets = [];

  static values = {
    autoOpen: Boolean,
    foundationShades: {
      type: Object,
      default: {}
    },
    locale: {
      type: String,
      default: 'en_US'
    },
    isMobile: Boolean,
    isYMKModuleLoaded: Boolean,
    isYMKModuleOpen: Boolean,
    shadeLocalizedPath: {
      type: String,
      default: ''
    },
    shades: {
      type: Array,
      default: []
    },
    activeVariant: String,
    apikey: String,
    id: String,
    type: String,
    skincareResults: Array,
    productIdList: {
      type: Array,
      default: ['22830']
    },
    width: Number,
    height: Number,
    enabled: {
      type: Boolean,
      default: false
    }
  };

  // TARGETS
  declare launchButtonTarget: HTMLButtonElement;
  declare productCarouselTarget: HTMLDivElement;
  declare shadesDropdownTextTarget: HTMLSpanElement;
  declare vtoContainerTarget: HTMLDivElement;
  declare variantShadeTargets: HTMLAnchorElement[];
  declare shadesDropdownSelectorTargets: HTMLAnchorElement[];
  declare triggerTarget: HTMLButtonElement;
  declare hasLaunchButtonTarget: boolean;
  declare hasProductCarouselTarget: boolean;
  declare hasShadesDropdownTextTarget: boolean;
  declare hasVtoContainerTarget: boolean;
  declare hasVariantShadeTargets: boolean;
  declare hasShadesDropdownSelectorTargets: boolean;
  declare hasTriggerTarget: boolean;

  // VALUES
  private activeVariantValue: string | undefined;
  private apikeyValue: string;
  private foundationShadesValue: IFoundationShade;
  private idValue: string;
  private isYMKModuleLoadedValue: boolean;
  private enabledValue: boolean;
  private localeValue: string;
  private mediaValue: string;
  private payloadEventValueCameraEnd: number;
  private payloadEventValueCameraStart: number;
  private productIdListValue: string[];
  private shadeLocalizedPathValue: string;
  private typeValue: string;

  initialize() {
    if (this.enabledValue) {
      this.loadYMK();
    }
  }

  isMobile() {
    return screen.width <= MOBILE_BREAKPOINT;
  }

  isDesktop() {
    return screen.width >= DESKTOP_BREAKPOINT;
  }

  launchVTO() {
    if (this.hasVtoContainerTarget) {
      this.vtoContainerTarget.style.display = '';
    }

    if (this.hasLaunchButtonTarget) {
      this.launchButtonTarget.disabled = true;
    }

    const width = this.vtoContainerTarget.offsetWidth;
    const height = this.vtoContainerTarget.offsetHeight;
    this.productCarouselTarget.style.visibility = 'hidden';
    this.createYMKElement();

    if (this.isYMKModuleLoadedValue) {
      this.openVTOByType(this.typeValue);
    }

    window.ymkAsyncInit = () => {
      window.YMK.init({
        autoOpen: this.typeValue !== SKINCARE,
        showCloseBtnOnLauncher: true,
        language: this.languageMapper(this.localeValue),
        ...(this.typeValue === MAKEUP && this.isDesktop() && { height }),
        ...(this.typeValue === MAKEUP && this.isDesktop() && { width }),
        ...(this.typeValue === SKINCARE && this.isMobile() && { height: screen.height }),
        ...(this.typeValue === SKINCARE && this.isMobile() && { width: screen.width }),
        ...(this.typeValue === FOUNDATION && { productIdList: this.productIdListValue })
      });

      if (this.typeValue === FOUNDATION || this.typeValue === SKINCARE) {
        this.openVTOByType(this.typeValue);
      }

      window.dispatchEvent(
        new CustomEvent(VTO_ACTIONS_TRACKING_EVENT, {
          detail: {
            event_action: CTA_ACTION,
            vto_type: this.typeValue,
            vto_action: CTA_ACTION
          }
        })
      );

      this.addYMKEventListeners();
    };

    window.ymkAsyncInit();
  }

  loadYMK() {
      if (!this.idValue || !this.apikeyValue) {
        return;
      }

      const apiUrl = `https://plugins-media.makeupar.com/${this.idValue}/sdk.js?apiKey=${this.apikeyValue}`;
      const scriptLoaded = Array.from(document.getElementsByTagName('script')).find(
        (script) => script.src === apiUrl
      );

      if (scriptLoaded) {
        return;
      }

      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = apiUrl;
      document.body.appendChild(s);
  }

  getURLSubdirectoryByType(type) {
    switch (type) {
      case MAKEUP:
        return '';
      case FOUNDATION:
        return '-shadefinder';
      case SKINCARE:
        return '-skincare';
      default:
        return '';
    }
  }

  openVTOByType(type) {
    switch (type) {
      case MAKEUP:
        return window.YMK.open();
      case FOUNDATION:
        return window.YMK.open(true, null, true);
      case SKINCARE:
        return window.YMK.openSkincare('capture');
      default:
        return window.YMK.open();
    }
  }

  createYMKElement() {
    const ymkElement = document.getElementById(MODULE_ID);
    if (ymkElement != null) {
      return;
    }

    const YMKElement = document.createElement('div');
    const height = this.vtoContainerTarget.offsetHeight;

    YMKElement.id = MODULE_ID;
    YMKElement.style.height = height + 'px';

    this.vtoContainerTarget.appendChild(YMKElement);
  }

  addYMKEventListeners() {
    const vtoEvents = [
      CHOOSE_MODEL_ICON_CLICKED_EVENT,
      LIVE_CAM_ICON_CLICKED_EVENT,
      LOAD_IMAGE_BY_MODEL_EVENT,
      UPLOAD_PHOTO_ICON_CLICKED_EVENT,
      'cameraClosed',
      'cameraFailed',
      'cameraOpened',
      'compare',
      'compareDisabled',
      'splitFourWay',
      'splitFourWayDisabled',
      'photoLoaded'
    ];

    vtoEvents.forEach(
      function (eventAction) {
        window.YMK.addEventListener(
          eventAction,
          function () {
            if (eventAction === LIVE_CAM_ICON_CLICKED_EVENT) {
              this.mediaValue = CAMERA;
            } else if (eventAction === CHOOSE_MODEL_ICON_CLICKED_EVENT) {
              this.mediaValue = MODEL;
            } else if (eventAction === UPLOAD_PHOTO_ICON_CLICKED_EVENT) {
              this.mediaValue = UPLOAD;
            }

            window.dispatchEvent(
              new CustomEvent(VTO_ACTIONS_TRACKING_EVENT, {
                detail: {
                  event_action: eventAction,
                  vto_type: this.typeValue,
                  vto_media_type: this.mediaValue
                }
              })
            );
          }.bind(this)
        );
      }.bind(this)
    );

    window.YMK.addEventListener(LOADING_EVENT, (progress) => {
      progress === 10 && this.resizeVTO(LOADING_EVENT);
    });

    window.YMK.addEventListener(LOADED_EVENT, () => {
      this.isYMKModuleLoadedValue = true;
      this.payloadEventValueCameraEnd = performance.now();
      const eventValue = Math.floor(
        this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart
      );
      this.resizeVTO(LOADED_EVENT, eventValue);

      if (this.typeValue === MAKEUP) {
        window.YMK.applyMakeupBySku(this.activeVariantValue);

        for (const [index, element] of this.variantShadeTargets.entries()) {
          const vtoEnabled = element.dataset['vtoShadeEnabled'];
          delete element.dataset['phxLink'];
          delete element.dataset['phxLinkState'];

          delete this.shadesDropdownSelectorTargets[index].dataset['phxLink'];
          delete this.shadesDropdownSelectorTargets[index].dataset['phxLinkState'];
          this.shadesDropdownSelectorTargets[index].dataset.action =
            'shades-dropdown#toggleListDisplay';

          const localizedPath = element.href;
          const label = element.querySelector('label');
          element.href = '#';
          this.shadesDropdownSelectorTargets[index].href = '#';

          if (vtoEnabled) {
            const selectedSku = element.dataset['vtoSelectedSku'];
            const vtoSku = element.querySelector('input')?.value;
            const shadeDiv = element.querySelector('[data-shade]') as HTMLDivElement;
            const skuShade = shadeDiv?.dataset['shade'];

            if (vtoSku === this.activeVariantValue) {
              this.shadeLocalizedPathValue = localizedPath;
            }

            if (label) {
              label.style.cursor = 'pointer';
            }

            element.dataset.action = 'product#updateSelectedSkuViaEvent';
            element.dataset.skuid = selectedSku;

            const changeVTOShade = () => {
              this.activeVariantValue = vtoSku;
              this.shadeLocalizedPathValue = localizedPath;
              this.shadesDropdownTextTarget.innerText = label?.title || '';

              const shades_dropdowon = this.triggerTarget.querySelector(
                '[data-shade]'
              ) as HTMLDivElement;
              if (shades_dropdowon && skuShade) {
                shades_dropdowon.style.backgroundColor = skuShade;
              }

              for (const [i, el] of this.variantShadeTargets.entries()) {
                el.classList.remove('checked');

                const shadEl = this.shadesDropdownSelectorTargets[i].parentNode as HTMLElement;
                shadEl.classList.remove('checked');
              }
              element.classList.add('checked');

              const shadSelectorEl = this.shadesDropdownSelectorTargets[index]
                .parentNode as HTMLElement;
              shadSelectorEl.classList.add('checked');
              window.YMK.applyMakeupBySku(vtoSku);
            };

            element.onclick = changeVTOShade;
            this.shadesDropdownSelectorTargets[index].onclick = changeVTOShade;
          } else {
            element.style.opacity = '0.6';

            if (label) {
              label.style.cursor = 'not-allowed';
            }
          }
        }
      }
    });

    window.YMK.addEventListener(OPENED_EVENT, () => {
      this.resizeVTO(OPENED_EVENT);
    });

    window.YMK.addEventListener(ENGINE_CLOSED_EVENT, () => {
      this.resizeVTO(ENGINE_CLOSED_EVENT);
    });

    window.YMK.addEventListener(CHOOSE_MODEL_ICON_CLICKED_EVENT, () => {
      this.resizeVTO(CHOOSE_MODEL_ICON_CLICKED_EVENT);
    });

    window.YMK.addEventListener(LOAD_IMAGE_BY_MODEL_EVENT, () => {
      this.resizeVTO(LOAD_IMAGE_BY_MODEL_EVENT);
    });

    window.YMK.addEventListener(SKIN_SHADE_FOUND_EVENT, (result) => {
      this.foundationShadesValue = result.readableRecommendations[0];
      window.YMK.applyMakeupBySku(result.readableRecommendations[0].guid);
      this.generateFoundationShadeOverlay();
    });

    window.YMK.addEventListener(SKIN_ANALYSIS_FINISHED_EVENT, (/*result*/) => {
      document.body.style.overflow = '';
    });

    window.YMK.addEventListener(CLOSED_EVENT, () => {
      this.vtoContainerTarget.style.display = '';
      this.launchButtonTarget.disabled = false;
      this.payloadEventValueCameraEnd = performance.now();

      window.dispatchEvent(
        new CustomEvent(VTO_ACTIONS_TRACKING_EVENT, {
          detail: {
            event_action: CLOSED_EVENT,
            vto_type: this.typeValue,
            vto_media_type: this.mediaValue,
            event_value: Math.floor(
              this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart
            )
          }
        })
      );

      if (this.shadeLocalizedPathValue) {
        window.location.replace(this.shadeLocalizedPathValue);
        // if location.replace fails force the page to reload after two seconds
        setTimeout(() => {
          location.reload();
        }, 2000);
      } else {
        this.resetYMK();
      }
    });
  }

  resetYMK() {
    const ymkElement = document.getElementById(MODULE_ID);
    this.vtoContainerTarget.style.display = 'none';
    this.productCarouselTarget.style.visibility = 'visible';
    ymkElement?.remove();
    window.YMK.reset();
  }

  generateFoundationShadeButton(hex, sku) {
    const button = document.createElement('button');
    button.style.background = hex;
    button.onclick = () => window.YMK.applyMakeupBySku(sku);

    return button;
  }

  generateFoundationShadeOverlay() {
    const shadeContainer = document.createElement('div');

    const cooler = this.generateFoundationShadeButton(
      foundationShades[0].hex,
      this.foundationShadesValue.cooler
    );
    const lighter = this.generateFoundationShadeButton(
      foundationShades[1].hex,
      this.foundationShadesValue.lighter
    );
    const best = this.generateFoundationShadeButton(
      foundationShades[2].hex,
      this.foundationShadesValue.guid
    );
    const darker = this.generateFoundationShadeButton(
      foundationShades[3].hex,
      this.foundationShadesValue.darker
    );
    const warmer = this.generateFoundationShadeButton(
      foundationShades[4].hex,
      this.foundationShadesValue.warmer
    );

    shadeContainer.appendChild(cooler);
    shadeContainer.appendChild(lighter);
    shadeContainer.appendChild(best);
    shadeContainer.appendChild(darker);
    shadeContainer.appendChild(warmer);

    const YMKElement = document.getElementById(MODULE_ID);
    const YMKiFrame = document.getElementById(MODULE_IFRAME_ID);

    const wrapper = document.createElement('div');

    wrapper.appendChild(shadeContainer);

    const addToBagTemplate = document.getElementById(ATB_BUTTON_ID);
    const addToBagTemplateContent = (addToBagTemplate as HTMLTemplateElement).content;
    const clonedContent = addToBagTemplateContent.cloneNode(true);

    wrapper.append(shadeContainer);
    wrapper.appendChild(clonedContent);

    if (YMKiFrame) {
      YMKiFrame.style.height = `${screen.height - 208}px`;
    }
    YMKElement?.appendChild(wrapper);
  }

  resizeVTO(event_action: string, event_value = 0) {
    const YMKElement = document.getElementById(MODULE_ID);
    const DELAY = 500;
    if (!YMKElement) {
      return;
    }
    this.payloadEventValueCameraStart = performance.now();

    window.dispatchEvent(
      new CustomEvent(VTO_ACTIONS_TRACKING_EVENT, {
        detail: {
          event_action,
          vto_type: this.typeValue,
          vto_media_type: this.mediaValue,
          event_value
        }
      })
    );

    YMKElement.style.display = 'none';

    setTimeout(() => {
      const YMKiFrame = document.getElementById(MODULE_IFRAME_ID) as HTMLIFrameElement;
      const YMKVideoInput = document.getElementById(MODULE_VIDEO_INPUT_ID) as HTMLIFrameElement;

      if (YMKiFrame) {
        const height = this.vtoContainerTarget.offsetHeight;
        const width = this.vtoContainerTarget.offsetWidth;

        const YMKElementFirstDiv = YMKElement.querySelector('div');
        const iframeDocument = YMKiFrame?.contentWindow?.document;
        const frameContent = iframeDocument?.querySelector('.frame-content');
        const frameFirstDiv = frameContent && frameContent.querySelector('div');
        const frameContentChild = frameFirstDiv && frameFirstDiv.querySelector('div');
        const frameContentLastGrandchild: HTMLElement | null | undefined =
          frameContentChild?.querySelector('div:last-child');
        const resultCanvas = iframeDocument?.getElementById(CANVAS_ID);

        if (event_action === CHOOSE_MODEL_ICON_CLICKED_EVENT) {
          const imgs = frameContent?.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
          if (imgs) {
            imgs.forEach((img) => {
              img.style.width = this.isMobile() ? '65%' : '85%';
              img.style.display = 'flex';
              img.style.justifySelf = 'center';
            });
          }
        }

        if (YMKElementFirstDiv) {
          YMKElementFirstDiv.style.height = height + 'px';
          YMKElementFirstDiv.style.width = width + 'px';
        }

        if (frameFirstDiv) {
          frameFirstDiv.style.height = height + 'px';
          frameFirstDiv.style.width = width + 'px';
        }

        YMKiFrame.style.height = height + 'px';
        YMKiFrame.style.width = width + 'px';

        if (YMKVideoInput) {
          YMKVideoInput.style.height = height + 'px';
          YMKVideoInput.style.width = width + 'px';
        }

        if (frameContentLastGrandchild) {
          frameContentLastGrandchild.style.top = '60%';
        }

        if (resultCanvas) {
          resultCanvas.style.height = height + 'px';
          resultCanvas.style.width = width + 'px';
        }
      }

      YMKElement.style.display = '';
    }, DELAY);
  }

  languageMapper(locale) {
    const languageMap = {
      cs_: 'cze',
      de_: 'deu',
      da_: 'dnk',
      en_: 'enu',
      es_: 'esp',
      fi_: 'fin',
      fr_: 'fra',
      it_: 'ita',
      ja_: 'jpn',
      ko_: 'kor',
      nl_: 'dut',
      no_: 'nor',
      pl_: 'pol',
      pt_: 'ptb',
      zh_: 'cht',
      zhhans: 'chs',
      zhhant: 'cht'
    };

    for (const key in languageMap) {
      if (locale.includes(key)) {
        return languageMap[key];
      }
    }

    return 'enu';
  }
}
